import React, { useState, useEffect } from 'react';
import { Download, Calendar, Globe, TrendingUp, Users, FileText, Search, Activity, ExternalLink } from 'lucide-react';
import { getHtmlReportFromGitHub } from '../utils/adaGithubStorage';

const AdminAnalytics = () => {
  const [searchData, setSearchData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [dateFilter, setDateFilter] = useState('all');
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [htmlReport, setHtmlReport] = useState(null);
  const [loadingHtmlReport, setLoadingHtmlReport] = useState(false);

  useEffect(() => {
    loadSearchData();
  }, []);

  useEffect(() => {
    filterData();
  }, [searchData, dateFilter, searchFilter]);

  const loadSearchData = async () => {
    try {
      const response = await fetch('/analytics');
      if (response.ok) {
        const result = await response.json();
        // Handle both old format and new format from analytics API
        const data = result.data || result;
        // Ensure data is always an array
        setSearchData(Array.isArray(data) ? data : []);
      } else {
        console.error('Failed to load analytics data:', response.status, response.statusText);
        setSearchData([]);
      }
    } catch (error) {
      console.error('Error loading analytics data:', error);
      setSearchData([]);
    }
  };

  const filterData = () => {
    // Ensure searchData is an array
    const dataArray = Array.isArray(searchData) ? searchData : [];
    let filtered = [...dataArray];

    // Date filtering
    if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (dateFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          filtered = filtered.filter(item => new Date(item.timestamp) >= filterDate);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          filtered = filtered.filter(item => new Date(item.timestamp) >= filterDate);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          filtered = filtered.filter(item => new Date(item.timestamp) >= filterDate);
          break;
      }
    }

    // Search filtering
    if (searchFilter) {
      filtered = filtered.filter(item => 
        item.url.toLowerCase().includes(searchFilter.toLowerCase()) ||
        (item.userAgent && item.userAgent.toLowerCase().includes(searchFilter.toLowerCase())) ||
        (item.analysisResult && JSON.stringify(item.analysisResult).toLowerCase().includes(searchFilter.toLowerCase()))
      );
    }

    setFilteredData(filtered);
  };

  const exportData = (format) => {
    const dataToExport = filteredData.length > 0 ? filteredData : searchData;
    
    if (format === 'csv') {
      const csvContent = [
        'URL,Timestamp,User Agent,Screen Resolution,Score,Total Issues,Errors,Warnings,Info',
        ...dataToExport.map(item => [
          `"${item.url}"`,
          `"${new Date(item.timestamp).toISOString()}"`,
          `"${item.userAgent || ''}"`,
          `"${item.screenResolution || ''}"`,
          item.analysisResult?.score || '',
          item.analysisResult?.totalIssues || '',
          item.analysisResult?.errors || '',
          item.analysisResult?.warnings || '',
          item.analysisResult?.info || ''
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } else if (format === 'json') {
      const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const getAnalyticsSummary = () => {
    // Ensure searchData is an array
    const dataArray = Array.isArray(searchData) ? searchData : [];
    
    const total = dataArray.length;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayCount = dataArray.filter(item => new Date(item.timestamp) >= today).length;
    
    const avgScore = dataArray.length > 0 
      ? Math.round(dataArray.reduce((sum, item) => sum + (item.analysisResult?.score || 0), 0) / dataArray.length)
      : 0;
    
    const uniqueDomains = new Set(dataArray.map(item => {
      try {
        return new URL(item.url).hostname;
      } catch {
        return item.url;
      }
    })).size;
    
    return { total, todayCount, avgScore, uniqueDomains };
  };

  const summary = getAnalyticsSummary();

  const handleViewDetails = async (item) => {
    setSelectedAnalysis(item);
    setHtmlReport(null);
    
    // If we have a GitHub file, try to load the HTML report
    if (item.githubFile) {
      setLoadingHtmlReport(true);
      try {
        const result = await getHtmlReportFromGitHub(item.githubFile);
        if (result.success) {
          setHtmlReport(result.htmlContent);
        } else {
          console.warn('Failed to load HTML report:', result.error);
        }
      } catch (error) {
        console.error('Error loading HTML report:', error);
      } finally {
        setLoadingHtmlReport(false);
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-lg">
              <Search className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Total Searches</p>
              <p className="text-2xl font-bold text-gray-900">{summary.total}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-lg">
              <Calendar className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Today's Searches</p>
              <p className="text-2xl font-bold text-gray-900">{summary.todayCount}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="bg-yellow-100 p-3 rounded-lg">
              <TrendingUp className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Average Score</p>
              <p className="text-2xl font-bold text-gray-900">{summary.avgScore}%</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="bg-purple-100 p-3 rounded-lg">
              <Globe className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Unique Domains</p>
              <p className="text-2xl font-bold text-gray-900">{summary.uniqueDomains}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-8">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex flex-wrap gap-4">
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
            
            <input
              type="text"
              placeholder="Search URLs or details..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => exportData('csv')}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </button>
            <button
              onClick={() => exportData('json')}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              <Download className="w-4 h-4 mr-2" />
              Export JSON
            </button>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Analysis History ({filteredData.length})
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  URL
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Issues
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.length > 0 ? (
                filteredData.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="max-w-xs truncate">
                        {item.url}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(item.timestamp).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                        (item.analysisResult?.score || 0) >= 90 
                          ? 'bg-green-100 text-green-800'
                          : (item.analysisResult?.score || 0) >= 70
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {item.analysisResult?.score || 'N/A'}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.analysisResult?.totalIssues || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleViewDetails(item)}
                        className="text-blue-600 hover:text-blue-900 font-medium"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    <div className="text-center py-8 text-gray-500">
                      No analysis data found. Run some accessibility tests to see analytics here.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Analysis Details Modal */}
      {selectedAnalysis && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold">Analysis Details</h3>
                <div className="flex items-center gap-2">
                  {selectedAnalysis.htmlReportUrl && (
                    <a
                      href={selectedAnalysis.htmlReportUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Open Full Report
                    </a>
                  )}
                  <button
                    onClick={() => {
                      setSelectedAnalysis(null);
                      setHtmlReport(null);
                    }}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="font-medium text-gray-700">URL:</label>
                  <p className="text-gray-600 break-all">{selectedAnalysis.url}</p>
                </div>
                <div>
                  <label className="font-medium text-gray-700">Timestamp:</label>
                  <p className="text-gray-600">{new Date(selectedAnalysis.timestamp).toLocaleString()}</p>
                </div>
                {selectedAnalysis.analysisResult && (
                  <>
                    <div>
                      <label className="font-medium text-gray-700">Score:</label>
                      <p className="text-gray-600">{selectedAnalysis.analysisResult.score}%</p>
                    </div>
                    <div>
                      <label className="font-medium text-gray-700">Total Issues:</label>
                      <p className="text-gray-600">{selectedAnalysis.analysisResult.totalIssues}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            <div className="flex-1 overflow-auto p-6">
              {loadingHtmlReport ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading detailed HTML report...</p>
                  </div>
                </div>
              ) : htmlReport ? (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b">
                    <h4 className="font-medium text-gray-700">Detailed HTML Report</h4>
                  </div>
                  <div className="bg-white">
                    <iframe
                      srcDoc={htmlReport}
                      className="w-full h-96 border-0"
                      title="HTML Accessibility Report"
                      sandbox="allow-same-origin"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedAnalysis.userAgent && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">User Agent:</label>
                      <p className="text-sm text-gray-600 mt-1">{selectedAnalysis.userAgent}</p>
                    </div>
                  )}
                  
                  {selectedAnalysis.screenResolution && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Screen Resolution:</label>
                      <p className="text-sm text-gray-600 mt-1">{selectedAnalysis.screenResolution}</p>
                    </div>
                  )}
                  
                  {selectedAnalysis.analysisResult && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Analysis Results:</label>
                      <div className="bg-gray-50 p-4 rounded-lg mt-2">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="font-medium">Score:</span>
                            <span className="ml-2">{selectedAnalysis.analysisResult.score}%</span>
                          </div>
                          <div>
                            <span className="font-medium">Total Issues:</span>
                            <span className="ml-2">{selectedAnalysis.analysisResult.totalIssues}</span>
                          </div>
                          <div>
                            <span className="font-medium">Errors:</span>
                            <span className="ml-2">{selectedAnalysis.analysisResult.errors}</span>
                          </div>
                          <div>
                            <span className="font-medium">Warnings:</span>
                            <span className="ml-2">{selectedAnalysis.analysisResult.warnings}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedAnalysis.githubFile ? (
                    <div>
                      <label className="text-sm font-medium text-gray-700">GitHub Storage:</label>
                      <p className="text-sm text-gray-600 mt-1">
                        Report stored as: <code className="bg-gray-100 px-1 rounded">{selectedAnalysis.githubFile}</code>
                      </p>
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-800">
                        🔍 HTML report not available. This may be an older scan result.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAnalytics;